export const PWA_INSTALL_PROMPT_CHANGED_EVENT = "metajuntos:pwa-install-prompt-changed";

export type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed"; platform: string }>;
};

export type PwaInstallPromptState = {
  promptAvailable: boolean;
  isStandalone: boolean;
  installDismissed: boolean;
};

const DISMISS_KEY = "metajuntos-install-dismissed-until";
const LEGACY_DISMISS_KEY = "metajuntos-install-dismissed";
const DAY_IN_MS = 24 * 60 * 60 * 1000;

let initialized = false;
let installPrompt: BeforeInstallPromptEvent | null = null;
let isStandalone = false;
const listeners = new Set<() => void>();

function notify() {
  listeners.forEach((listener) => listener());
  window.dispatchEvent(new CustomEvent(PWA_INSTALL_PROMPT_CHANGED_EVENT));
}

function getDismissedUntil() {
  const raw = localStorage.getItem(DISMISS_KEY);
  const value = raw ? Number(raw) : 0;
  return Number.isFinite(value) ? value : 0;
}

function isInstallDismissed() {
  return getDismissedUntil() > Date.now();
}

function updateStandaloneState() {
  isStandalone =
    window.matchMedia("(display-mode: standalone)").matches ||
    Boolean((navigator as Navigator & { standalone?: boolean }).standalone);
}

export function getPwaInstallPromptState(): PwaInstallPromptState {
  return {
    promptAvailable: Boolean(installPrompt),
    isStandalone,
    installDismissed: isInstallDismissed(),
  };
}

export function subscribePwaInstallPrompt(listener: () => void) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function initializePwaInstallPrompt() {
  if (initialized) return;
  if (typeof window === "undefined") return;

  initialized = true;
  updateStandaloneState();

  if (localStorage.getItem(LEGACY_DISMISS_KEY) === "true") {
    localStorage.removeItem(LEGACY_DISMISS_KEY);
  }

  const displayModeQuery = window.matchMedia("(display-mode: standalone)");
  const handleDisplayModeChange = () => {
    updateStandaloneState();
    notify();
  };

  displayModeQuery.addEventListener?.("change", handleDisplayModeChange);

  window.addEventListener("beforeinstallprompt", (event) => {
    event.preventDefault();

    if (isInstallDismissed()) {
      return;
    }

    installPrompt = event as BeforeInstallPromptEvent;
    notify();
  });

  window.addEventListener("appinstalled", () => {
    installPrompt = null;
    localStorage.removeItem(DISMISS_KEY);
    updateStandaloneState();
    notify();
  });
}

export async function promptPwaInstall() {
  if (!installPrompt) return null;

  const prompt = installPrompt;
  await prompt.prompt();
  const choice = await prompt.userChoice;

  installPrompt = null;

  if (choice.outcome === "accepted") {
    localStorage.removeItem(DISMISS_KEY);
  } else {
    dismissPwaInstallPrompt(1);
  }

  notify();
  return choice;
}

export function dismissPwaInstallPrompt(days = 7) {
  installPrompt = null;
  localStorage.setItem(DISMISS_KEY, String(Date.now() + days * DAY_IN_MS));
  notify();
}

export function resetPwaInstallDismissal() {
  localStorage.removeItem(DISMISS_KEY);
  localStorage.removeItem(LEGACY_DISMISS_KEY);
  notify();
}
